import { useEffect, useState, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import { useNavigate, useParams } from 'react-router-dom'
import {ArrowLeft, Edit2, TreePine, User, Clock, CheckCircle, Loader2, Plus, X, AlertTriangle, Printer, FileDown, MapPin, FlagOff, UploadCloud, CalendarCheck, FileCheck2, FileText, Paperclip, ChevronRight, Eye, UserCheck, MessageSquare, Sparkles} from 'lucide-react'
import { formatarData, formatarDataHora } from '@/utils/protocolo'
import type { Solicitacao, HistoricoStatus, StatusSolicitacao, Configuracao } from '@/types'
import { STATUS_COLORS, STATUS_LIST } from '@/types'
import toast from 'react-hot-toast'
import { gerarPDF } from '@/utils/gerarPDF'

const BRAND = '#0f3b50'

export default function VisualizarSolicitacao() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [sol, setSol] = useState<Solicitacao | null>(null)
  const [historico, setHistorico] = useState<HistoricoStatus[]>([])
  const [loading, setLoading] = useState(true)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [novoStatus, setNovoStatus] = useState<StatusSolicitacao | ''>('')
  const [responsavel, setResponsavel] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [savingStatus, setSavingStatus] = useState(false)
  const [gerandoPDF, setGerandoPDF] = useState(false)
  // Finalização
  const [showFinalizarModal, setShowFinalizarModal] = useState(false)
  const [pdfAssinado, setPdfAssinado] = useState<File | null>(null)
  const [docsAnexos, setDocsAnexos] = useState<File[]>([])
  const [obsFinalizacao, setObsFinalizacao] = useState('')
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [finalizando, setFinalizando] = useState(false)
  const pdfInputRef = useRef<HTMLInputElement>(null)
  const docsInputRef = useRef<HTMLInputElement>(null)

  const fetchData = async () => {
    if (!id) return
    try {
      const [solRes, histRes] = await Promise.all([
        lumi.entities.solicitacoes.list({ limit: 9999 }),
        lumi.entities.historico_status.list({ filter: { solicitacaoId: id }, sort: { createdAt: -1 }, limit: 50 }),
      ])
      setSol(((solRes.list || []) as Solicitacao[]).find(s => s._id === id) || null)
      setHistorico((histRes.list || []) as HistoricoStatus[])
    } catch (err) { console.error('Erro ao carregar solicitação:', err) }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchData() }, [id])

  const handleUpdateStatus = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!novoStatus) { toast.error('Selecione o novo status.'); return }
    if (!responsavel.trim()) { toast.error('Informe o responsável.'); return }
    if (!observacoes.trim()) { toast.error('A observação é obrigatória e ficará visível ao solicitante.'); return }
    if (!sol) return
    setSavingStatus(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.solicitacoes.update(sol._id, { status: novoStatus, updatedAt: now })
      await lumi.entities.historico_status.create({
        solicitacaoId: sol._id, protocolo: sol.protocolo, status: novoStatus,
        responsavel, observacoes: observacoes || '', creator: 'sistema', createdAt: now, updatedAt: now
      })
      toast.success('Status atualizado!')
      setShowStatusModal(false)
      setNovoStatus(''); setResponsavel(''); setObservacoes('')
      fetchData()
    } catch { toast.error('Erro ao atualizar status.') }
    finally { setSavingStatus(false) }
  }

  const handleGerarPDF = async () => {
    if (!sol) return
    setGerandoPDF(true)
    try {
      let cfg: Configuracao | null = null
      try { const r = await lumi.entities.configuracoes.list({ limit: 1 }); cfg = (r.list?.[0] as Configuracao) || null } catch { /**/ }
      await gerarPDF(sol, historico, cfg)
      toast.success('PDF gerado com sucesso!')
    } catch { toast.error('Erro ao gerar PDF.') }
    finally { setGerandoPDF(false) }
  }

  const handleFinalizar = async () => {
    if (!sol) return
    setFinalizando(true)
    let pdfUrl = ''
    // Upload do PDF apenas se o utilizador seleccionou um ficheiro
    if (pdfAssinado) {
      setUploadingPdf(true)
      try {
        const results = await lumi.tools.file.upload([pdfAssinado])
        pdfUrl = results[0]?.fileUrl || ''
        if (!pdfUrl) throw new Error('URL vazia')
      } catch {
        toast.error('Erro ao fazer upload do PDF assinado.')
        setUploadingPdf(false)
        setFinalizando(false)
        return
      }
      setUploadingPdf(false)
    }
    // Upload de documentos anexos
    let docUrls: string[] = []
    if (docsAnexos.length > 0) {
      try {
        const results = await lumi.tools.file.upload(docsAnexos)
        docUrls = results.map((r: any) => r.fileUrl).filter(Boolean)
      } catch {
        toast.error('Erro ao fazer upload dos documentos.')
        setFinalizando(false)
        return
      }
    }
    try {
      const now = new Date().toISOString()
      const updatePayload: Record<string, unknown> = {
        finalizado: true,
        dataFinalizacao: now,
        observacaoFinalizacao: obsFinalizacao || '',
        status: 'Processo Concluído',
        updatedAt: now,
      }
      if (pdfUrl) updatePayload.pdfAssinadoUrl = pdfUrl
      if (docUrls.length > 0) {
        const existentes: string[] = (sol as any).documentos || []
        updatePayload.documentos = [...existentes, ...docUrls]
      }
      await lumi.entities.solicitacoes.update(sol._id, updatePayload)
      await lumi.entities.historico_status.create({
        solicitacaoId: sol._id, protocolo: sol.protocolo,
        status: 'Processo Concluído' as StatusSolicitacao,
        responsavel: 'Sistema',
        observacoes: obsFinalizacao || (pdfUrl ? 'Processo finalizado com PDF assinado.' : 'Processo finalizado.'),
        creator: 'sistema', createdAt: now, updatedAt: now,
      })
      toast.success('Processo finalizado com sucesso!')
      setShowFinalizarModal(false)
      setPdfAssinado(null); setDocsAnexos([]); setObsFinalizacao('')
      fetchData()
    } catch { toast.error('Erro ao finalizar o processo.') }
    finally { setFinalizando(false) }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: BRAND }} />
      <span className="text-sm text-gray-500">Carregando solicitação...</span>
    </div>
  )

  if (!sol) return (
    <div className="text-center py-20">
      <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
      <h3 className="text-base font-semibold text-gray-800 mb-1">Solicitação não encontrada</h3>
      <p className="text-sm text-gray-500 mb-5">O protocolo solicitado não existe ou foi removido.</p>
      <button onClick={() => navigate('/solicitacoes')}
        className="px-5 py-2.5 text-white text-sm font-medium rounded-2xl transition"
        style={{ backgroundColor: BRAND }}>
        Voltar para Solicitações
      </button>
    </div>
  )

  const statusCls = STATUS_COLORS[sol.status] || 'bg-gray-50 text-gray-700 border-gray-200'
  const jaFinalizado = !!sol.finalizado

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/solicitacoes')}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-2xl transition">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 font-mono">{sol.protocolo}</h1>
            <p className="text-xs text-gray-400 mt-0.5">Solicitação de Remoção de Árvore</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {jaFinalizado && (
            <span className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-semibold bg-green-50 text-green-700 border-green-200">
              <FileCheck2 className="w-3 h-3" /> Finalizado
            </span>
          )}
          <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${statusCls}`}>{sol.status}</span>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-xs font-medium rounded-2xl hover:bg-gray-50 transition">
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Imprimir</span>
          </button>
          <button onClick={handleGerarPDF} disabled={gerandoPDF}
            className="flex items-center gap-1.5 px-3 py-2 text-white text-xs font-medium rounded-2xl transition"
            style={{ backgroundColor: gerandoPDF ? '#9ca3af' : BRAND }}>
            {gerandoPDF ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{gerandoPDF ? 'Gerando...' : 'Baixar PDF'}</span>
          </button>
          {!jaFinalizado && (
            <button onClick={() => setShowFinalizarModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-white text-xs font-semibold rounded-2xl transition shadow-sm"
              style={{ backgroundColor: '#16a34a' }}>
              <FlagOff className="w-3.5 h-3.5" />
              <span>Finalizar Processo</span>
            </button>
          )}
          <button onClick={() => navigate(`/solicitacoes/${sol._id}/editar`)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition">
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Banner finalização */}
      {jaFinalizado && (
        <div className="bg-green-50 border border-green-200 rounded-3xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <FileCheck2 className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800 text-sm">Processo Finalizado</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-green-700 mt-2">
              <div className="flex items-center gap-1.5">
                <CalendarCheck className="w-3.5 h-3.5" />
                <span><strong>Aberto em:</strong> {formatarData(sol.createdAt)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CalendarCheck className="w-3.5 h-3.5" />
                <span><strong>Finalizado em:</strong> {formatarData(sol.dataFinalizacao || '')}</span>
              </div>
            </div>
            {sol.observacaoFinalizacao && (
              <p className="text-xs text-green-700 mt-2 italic">"{sol.observacaoFinalizacao}"</p>
            )}
          </div>
          {sol.pdfAssinadoUrl && (
            <a href={sol.pdfAssinadoUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-800 text-white text-xs font-semibold rounded-2xl transition flex-shrink-0">
              <FileDown className="w-3.5 h-3.5" /> PDF Assinado
            </a>
          )}
        </div>
      )}

      {/* Dados Solicitante */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
        <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
          <User className="w-4 h-4" style={{ color: BRAND }} />
          Dados do Solicitante
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <InfoRow label="Nome Completo" value={sol.nomeCompleto} />
          <InfoRow label="CPF" value={sol.cpf} />
          <InfoRow label="Telefone" value={sol.telefone} />
          <InfoRow label="Data da Solicitação" value={formatarData(sol.createdAt)} />
          <div className="sm:col-span-2">
            <InfoRow label="Localização"
              value={[sol.ruaNome, sol.bairroNome, (sol as any).cidadeSolicitante].filter(Boolean).join(' – ')} />
          </div>
          {sol.numero && <InfoRow label="Número" value={sol.numero} />}
          {sol.pontoReferencia && (
            <div className="sm:col-span-2">
              <InfoRow label="Ponto de Referência" value={sol.pontoReferencia} />
            </div>
          )}
        </div>
      </div>

      {/* Dados Árvore */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
        <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
          <TreePine className="w-4 h-4" style={{ color: BRAND }} />
          Dados da Árvore
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <InfoRow label="Tipo" value={sol.tipoArvore} />
          <InfoRow label="Quantidade" value={String(sol.quantidadeArvores)} />
          {(sol as any).porte && <InfoRow label="Porte" value={(sol as any).porte} />}
          <InfoRow label="Estado da Árvore" value={sol.estadoArvore} />
          {(sol as any).coordenadas && (
            <div className="sm:col-span-2">
              <InfoRow label="Coordenadas GPS" value={(sol as any).coordenadas} />
            </div>
          )}
          <InfoRow label="Será Substituída" value={sol.seraSubstituida ? 'Sim' : 'Não'} />
          {sol.seraSubstituida && sol.localReplantio && (
            <InfoRow label="Local de Replantio"
              value={sol.localReplantio === 'mesmo' ? 'Mesmo Local' : `Outro Local: ${sol.ondeReplantio || ''}`} />
          )}
          {sol.seraSubstituida && sol.qualArvoreReplantio && (
            <InfoRow label="Árvore a Plantar" value={sol.qualArvoreReplantio} />
          )}
          {!sol.seraSubstituida && sol.motivoNaoSubstituicao && (
            <div className="sm:col-span-2">
              <InfoRow label="Motivo da Não Substituição" value={sol.motivoNaoSubstituicao} />
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Relato do Problema</p>
          <p className="text-sm text-gray-700 bg-gray-50 rounded-2xl p-4 leading-relaxed">{sol.relatoProblema}</p>
        </div>
      </div>

      {/* Fotos */}
      {sol.fotos && sol.fotos.length > 0 && (
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4" style={{ color: BRAND }} />
            Fotos Anexadas ({sol.fotos.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sol.fotos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                <img src={url} alt={`Foto ${i + 1}`} className="w-full h-32 object-cover transition group-hover:brightness-90" />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 py-1">
                  <span className="text-xs text-white font-medium px-2">Foto {i + 1}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Documentos Anexados */}
      {(sol as any).documentos && (sol as any).documentos.length > 0 && (
        <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
            <Paperclip className="w-4 h-4" style={{ color: BRAND }} />
            Documentos Anexados ({(sol as any).documentos.length})
          </h2>
          <div className="space-y-2">
            {(sol as any).documentos.map((url: string, i: number) => {
              const nome = url.split('/').pop() || `Documento ${i + 1}`
              return (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl hover:bg-[#e8f1f5] hover:border-[#0f3b50] transition group">
                  <FileText className="w-4 h-4 flex-shrink-0 group-hover:text-[#0f3b50] text-gray-400" />
                  <span className="text-xs text-gray-700 flex-1 truncate font-medium">{decodeURIComponent(nome)}</span>
                  <FileDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#0f3b50] flex-shrink-0" />
                </a>
              )
            })}
          </div>
        </div>
      )}

      {/* Assinaturas */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
        <h2 className="text-sm font-bold text-gray-900 mb-4">Assinaturas (Documento Físico)</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { title: sol.assinaturasSolicitante?.nome || sol.nomeCompleto, sub: `CPF: ${sol.assinaturasSolicitante?.cpf || sol.cpf}`, role: 'Solicitante' },
            { title: 'Jackson Dantas', sub: 'Prefeito Municipal', role: '' },
            { title: 'Secretário Responsável', sub: 'Secretário Municipal de Meio Ambiente', role: '' },
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <div className="w-full h-12 border-b-2 border-gray-300" />
              <div className="text-center">
                <p className="text-xs font-bold text-gray-800">{item.title}</p>
                <p className="text-xs text-gray-500">{item.sub}</p>
                {item.role && <p className="text-xs text-gray-400">{item.role}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status / Histórico */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: BRAND }} />
            Histórico de Status
          </h2>
          {!jaFinalizado && (
            <button onClick={() => setShowStatusModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-2xl transition"
              style={{ backgroundColor: BRAND }}>
              <Plus className="w-3.5 h-3.5" />
              Atualizar Status
            </button>
          )}
        </div>

        {historico.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Nenhum histórico registrado.</p>
        ) : (
          <div className="space-y-3">
            {historico.map((h, i) => {
              const cls = STATUS_COLORS[h.status as StatusSolicitacao] || 'bg-gray-50 text-gray-700 border-gray-200'
              return (
                <div key={h._id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                      style={{ backgroundColor: i === 0 ? BRAND : '#d1d5db' }} />
                    {i < historico.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
                  </div>
                  <div className="pb-3 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${cls}`}>{h.status}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{formatarDataHora(h.createdAt)}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1.5">
                      <span className="font-semibold">Responsável:</span> {h.responsavel}
                    </p>
                    {h.observacoes && <p className="text-xs text-gray-500 mt-0.5 italic">"{h.observacoes}"</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal Atualizar Status — Premium */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">

            {/* Faixa de cabeçalho contextual */}
            <div className="relative px-6 pt-6 pb-5" style={{ background: `linear-gradient(135deg, ${BRAND} 0%, #1a5a78 100%)` }}>
              {/* Decoração de fundo */}
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
                style={{ background: 'white', transform: 'translate(30%, -30%)' }} />
              <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full opacity-10"
                style={{ background: 'white', transform: 'translate(-30%, 30%)' }} />

              <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white leading-tight">Atualizar Status</h2>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>
                      Protocolo: <span className="font-mono font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>{sol.protocolo}</span>
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowStatusModal(false)}
                  className="p-1.5 rounded-2xl transition"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)')}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Status atual */}
              <div className="relative mt-4 flex items-center gap-2">
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>Status atual:</span>
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${STATUS_COLORS[sol.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                  {sol.status}
                </span>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                {novoStatus
                  ? <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${STATUS_COLORS[novoStatus as StatusSolicitacao] || 'bg-white text-gray-700 border-gray-200'}`}>{novoStatus}</span>
                  : <span className="text-xs px-2.5 py-1 rounded-full border font-medium" style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' }}>Novo status</span>
                }
              </div>
            </div>

            {/* Corpo do modal */}
            <form onSubmit={handleUpdateStatus} className="px-6 py-5 space-y-5">

              {/* Seleção de status — cards compactos */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f1f5' }}>
                    <span className="text-[10px] font-bold" style={{ color: BRAND }}>1</span>
                  </div>
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Novo Status <span className="text-red-500">*</span></p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {STATUS_LIST.map(s => {
                    const selected = novoStatus === s
                    const colorCls = STATUS_COLORS[s as StatusSolicitacao] || 'bg-gray-50 text-gray-700 border-gray-200'
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setNovoStatus(s as StatusSolicitacao)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl border text-left transition ${
                          selected
                            ? 'border-[#0f3b50] ring-2 ring-[#0f3b50]/15 bg-[#e8f1f5]'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${selected ? 'bg-[#0f3b50]' : 'bg-gray-300'}`} />
                        <span className={`text-xs font-medium leading-tight ${selected ? 'text-[#0f3b50]' : 'text-gray-600'}`}>{s}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Responsável */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f1f5' }}>
                    <span className="text-[10px] font-bold" style={{ color: BRAND }}>2</span>
                  </div>
                  <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Responsável <span className="text-red-500">*</span></p>
                </div>
                <div className="relative">
                  <UserCheck className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={responsavel}
                    onChange={e => setResponsavel(e.target.value)}
                    placeholder="Nome de quem está atualizando"
                    className="w-full pl-10 pr-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10 transition"
                  />
                </div>
              </div>

              {/* Observação */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8f1f5' }}>
                      <span className="text-[10px] font-bold" style={{ color: BRAND }}>3</span>
                    </div>
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Observação <span className="text-red-500">*</span></p>
                  </div>
                  {/* Badge visibilidade */}
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200">
                    <Eye className="w-3 h-3 text-amber-500" />
                    <span className="text-[10px] font-semibold text-amber-600">Visível ao cidadão</span>
                  </div>
                </div>
                <div className="relative">
                  <MessageSquare className="absolute left-3.5 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    value={observacoes}
                    onChange={e => setObservacoes(e.target.value)}
                    placeholder={
                      novoStatus === 'Em Análise'
                        ? 'Ex: Solicitação recebida e em análise pela equipe técnica.'
                        : novoStatus === 'Vistoria Agendada'
                        ? 'Ex: Vistoria agendada para o dia XX/XX às XX:XX.'
                        : novoStatus === 'Vistoria Realizada'
                        ? 'Ex: Vistoria realizada. Laudo técnico em elaboração.'
                        : novoStatus === 'Aprovada'
                        ? 'Ex: Solicitação aprovada. Remoção autorizada.'
                        : novoStatus === 'Reprovada'
                        ? 'Ex: Solicitação reprovada. Árvore saudável, sem risco estrutural.'
                        : novoStatus === 'Aguardando Replantio'
                        ? 'Ex: Remoção realizada. Aguardando replantio em até 15 dias.'
                        : novoStatus === 'Processo Concluído'
                        ? 'Ex: Processo finalizado. Replantio confirmado.'
                        : 'Descreva o andamento da solicitação...'
                    }
                    rows={4}
                    required
                    className="w-full pl-10 pr-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10 resize-none transition"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 pl-1">
                  Este comentário aparecerá no histórico público do protocolo.
                </p>
              </div>

              {/* Ações */}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowStatusModal(false)}
                  className="flex-1 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-2xl hover:bg-gray-50 transition">
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingStatus || !novoStatus || !responsavel.trim() || !observacoes.trim()}
                  className="flex-2 px-6 py-3 text-white text-sm font-bold rounded-2xl transition flex items-center justify-center gap-2 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: BRAND, flex: 2 }}>
                  {savingStatus
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
                    : <><CheckCircle className="w-4 h-4" /> Confirmar Atualização</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Finalizar Processo */}
      {showFinalizarModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <FlagOff className="w-5 h-5 text-green-600" />
                Finalizar Processo
              </h2>
              <button onClick={() => setShowFinalizarModal(false)}
                className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* datas info */}
            <div className="bg-gray-50 rounded-2xl p-4 mb-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="font-semibold">Data de abertura:</span>
                <span>{formatarData(sol.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="font-semibold">Data de finalização:</span>
                <span className="text-green-700 font-semibold">{formatarData(new Date().toISOString())} (hoje)</span>
              </div>
            </div>

            {/* upload PDF assinado */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                PDF do Processo Assinado
                <span className="ml-1.5 text-gray-400 font-normal">(opcional)</span>
              </label>
              <div
                onClick={() => pdfInputRef.current?.click()}
                className={`flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed rounded-2xl cursor-pointer transition ${
                  pdfAssinado ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-[#0f3b50] hover:bg-[#e8f1f5]'
                }`}
              >
                {uploadingPdf
                  ? <Loader2 className="w-6 h-6 text-[#0f3b50] animate-spin" />
                  : pdfAssinado
                    ? (
                      <div className="flex flex-col items-center gap-1">
                        <FileCheck2 className="w-6 h-6 text-green-600" />
                        <span className="text-xs text-green-700 font-semibold text-center px-2">{pdfAssinado.name}</span>
                        <span className="text-[10px] text-green-600">Clique para substituir</span>
                      </div>
                    )
                    : (
                      <div className="flex flex-col items-center gap-1">
                        <UploadCloud className="w-6 h-6 text-gray-400" />
                        <span className="text-xs text-gray-500">Clique para anexar o PDF assinado</span>
                        <span className="text-[10px] text-gray-400">Apenas ficheiros .pdf</span>
                      </div>
                    )
                }
              </div>
              <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) setPdfAssinado(f) }} />
            </div>

            {/* Documentos anexos */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Documentos Anexos
                <span className="ml-1.5 text-gray-400 font-normal">(opcional — PDF, DOC, DOCX, XLS, TXT)</span>
              </label>
              <div
                onClick={() => docsInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed rounded-2xl cursor-pointer transition border-gray-200 hover:border-[#0f3b50] hover:bg-[#e8f1f5]"
              >
                <Paperclip className="w-5 h-5 text-gray-400" />
                <span className="text-xs text-gray-500">Clique para anexar documentos</span>
                <span className="text-[10px] text-gray-400">{docsAnexos.length}/10 documentos</span>
              </div>
              <input ref={docsInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" multiple className="hidden"
                onChange={e => {
                  const files = Array.from(e.target.files || [])
                  if (docsAnexos.length + files.length > 10) { toast.error('Máximo de 10 documentos.'); return }
                  setDocsAnexos(prev => [...prev, ...files])
                }} />
              {docsAnexos.length > 0 && (
                <div className="mt-2 space-y-1">
                  {docsAnexos.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-2xl border border-gray-200">
                      <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
                      <button onClick={() => setDocsAnexos(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* observação */}
            <div className="mb-5">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Observação (opcional)</label>
              <textarea value={obsFinalizacao} onChange={e => setObsFinalizacao(e.target.value)} rows={3}
                placeholder="Adicione uma observação sobre a finalização do processo…"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none resize-none" />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowFinalizarModal(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={handleFinalizar} disabled={finalizando || uploadingPdf}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
                {(finalizando || uploadingPdf) ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {finalizando ? 'A finalizar…' : uploadingPdf ? 'A enviar PDF…' : 'Confirmar Finalização'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-gray-900 font-medium">{value || '-'}</p>
    </div>
  )
}
